import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { taskService } from "../../services";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
} from "../../utils";

dayjs.extend(utc);

type Props = {
    show: boolean;
    onClose: (added: boolean) => void;
    taskId?: string;
};

type TaskDateFormValues = {
    creationDate: string;
    dueDate: string;
    startTime: string;
    endTime: string;
};

const taskDatesValidationSchema = Yup.object({
    creationDate: Yup.string().required("Start date is required."),
    startTime: Yup.string().required("Start time is required."),
    dueDate: Yup.string()
        .required("Due date is required.")
        .test(
            "dateOrder",
            "Due date and time must be on or after the start date and time.",
            function (dueDateVal) {
                const p = this.parent as TaskDateFormValues;
                const { creationDate, startTime, endTime } = p;
                if (!creationDate || !startTime || !dueDateVal || !endTime) return true;
                const start = dayjs.utc(`${creationDate} ${startTime}`);
                const end = dayjs.utc(`${dueDateVal} ${endTime}`);
                if (!start.isValid() || !end.isValid()) return true;
                return end.isSame(start) || end.isAfter(start);
            }
        ),
    endTime: Yup.string().required("End time is required."),
});

const emptyDates: TaskDateFormValues = {
    creationDate: "",
    dueDate: "",
    startTime: "",
    endTime: "",
};

const ChangeTaskDates: React.FC<Props> = ({ show, onClose, taskId }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [currentTask, setCurrentTask] = useState<any>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => taskDatesValidationSchema, []);

    useEffect(() => {
        if (taskId) {
            taskService.get(taskId).then((task) => setCurrentTask(task));
        } else {
            setCurrentTask(undefined);
        }
    }, [taskId]);

    const initialValues: TaskDateFormValues = useMemo(() => {
        if (!currentTask) return emptyDates;
        return {
            creationDate: dayjs.utc(currentTask.creationDate).format("YYYY-MM-DD"),
            dueDate: dayjs.utc(currentTask.dueDate).format("YYYY-MM-DD"),
            startTime: dayjs.utc(currentTask.creationDate).format("HH:mm"),
            endTime: dayjs.utc(currentTask.dueDate).format("HH:mm"),
        };
    }, [currentTask]);

    const handleClose = () => onClose(false);

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose}>
            {taskId && !currentTask ? (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Change Dates</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="py-5 d-flex justify-content-center">
                        <LoadingSpinner text="Loading task…" />
                    </Modal.Body>
                </>
            ) : (
                <Formik<TaskDateFormValues>
                    enableReinitialize
                    key={currentTask?.id ?? "no-task"}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnMount={false}
                    validateOnChange
                    validateOnBlur={false}
                    onSubmit={async (values, { setStatus, setSubmitting }) => {
                        if (!currentTask) return;
                        setLiveValidationAfterSubmit(true);
                        setStatus(undefined);
                        setSubmitting(true);
                        try {
                            const response: any = await taskService.edit({
                                ...currentTask,
                                creationDate: dayjs.utc(`${values.creationDate} ${values.startTime}`).toDate(),
                                dueDate: dayjs.utc(`${values.dueDate} ${values.endTime}`).toDate(),
                            });
                            if (response.status !== "Failure") {
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess("Task dates updated successfully!");
                                setTimeout(() => onClose(true), 1000);
                            } else {
                                setAlertVariant("danger");
                                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                            }
                        } catch {
                            setAlertVariant("danger");
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ errors, touched, status, isSubmitting, handleSubmit, submitCount }) => {
                        const hasPersistedRecord = Boolean(currentTask?.id);
                        const showErr = (field: keyof TaskDateFormValues) =>
                            shouldShowFormFieldError(
                                errors,
                                field,
                                hasPersistedRecord,
                                submitCount,
                                liveValidationAfterSubmit,
                                touched
                            );
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Change Dates</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Form.Group as={Row} className="mb-3" controlId="taskCreationDate">
                                        <Form.Label column sm={3}>
                                            Start Time <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={4}>
                                            <Field
                                                name="creationDate"
                                                type="date"
                                                className={`form-control${showErr("creationDate") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("creationDate")}
                                            />
                                            {showErr("creationDate") && (
                                                <div className="invalid-feedback d-block">{errors.creationDate}</div>
                                            )}
                                        </Col>
                                        <Col sm={4}>
                                            <Field
                                                name="startTime"
                                                type="time"
                                                className={`form-control${showErr("startTime") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("startTime")}
                                            />
                                            {showErr("startTime") && (
                                                <div className="invalid-feedback d-block">{errors.startTime}</div>
                                            )}
                                        </Col>
                                    </Form.Group>

                                    <Form.Group as={Row} className="mb-3" controlId="taskDueDate">
                                        <Form.Label column sm={3}>
                                            Due/End Time <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={4}>
                                            <Field
                                                name="dueDate"
                                                type="date"
                                                className={`form-control${showErr("dueDate") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("dueDate")}
                                            />
                                            {showErr("dueDate") && (
                                                <div className="invalid-feedback d-block">{errors.dueDate}</div>
                                            )}
                                        </Col>
                                        <Col sm={4}>
                                            <Field
                                                name="endTime"
                                                type="time"
                                                className={`form-control${showErr("endTime") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("endTime")}
                                            />
                                            {showErr("endTime") && (
                                                <div className="invalid-feedback d-block">{errors.endTime}</div>
                                            )}
                                        </Col>
                                    </Form.Group>

                                    {status && (
                                        <Alert variant={alertVariant} className="mt-3">
                                            {status}
                                        </Alert>
                                    )}
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="form-group">
                                        <Button variant="primary" disabled={isSubmitting || !currentTask} type="submit" className="me-2">
                                            {isSubmitting ? <LoadingSpinner text="Changing Dates..." /> : "Change Dates"}
                                        </Button>
                                        <Button variant="secondary" onClick={handleClose}>
                                            Cancel
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
};

export { ChangeTaskDates };
